import { useQuery } from "@tanstack/react-query";
import { TableOfContentsItem } from "@/models/table-of-contents";
import { useItemService } from "./useServices";
import { PATH_KEYS } from "@/services/appServices";
import { logger } from "@/lib/logger";
import { restError } from "@/models/client/RestResponse";

interface UseTableOfContentsReturn {
  items: TableOfContentsItem[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook for fetching the table of contents for a document.
 * Read-only: retrieves TOC items by blobId from the API.
 */
export const useTableOfContents = (
  blobId?: string
): UseTableOfContentsReturn => {
  const tocService = useItemService((services) => services.toc);

  const { data, isLoading, error } = useQuery<TableOfContentsItem[]>({
    queryKey: [PATH_KEYS.toc, blobId],
    queryFn: async () => {
      if (!blobId) return [];
      const response = await tocService.getTocByBlobId(blobId);
      if (!response.success) {
        const error = restError(response);
        logger.error("Failed to load table of contents", {
          operation: "loadTableOfContents",
          error,
          blobId,
          status: response.status,
        });
        throw error;
      }
      return response.value ?? [];
    },
    enabled: !!blobId,
  });

  return {
    items: data ?? [],
    isLoading,
    error: error as Error | null,
  };
};
